/* One coordinator survives cart section replacement. Critical state is saved to Shopify. */
(() => {
  if (window.EasyUSADropship) return;
  const root = window.Shopify?.routes?.root || '/';
  const shippingModes = ['EasyUSA', 'Customer UPS Account', 'Customer Labels'];
  const shippingMode = value => shippingModes.includes(value) ? value : 'EasyUSA';
  const validAccount = value => /^[A-Z0-9]{6,10}$/.test(value || '');
  const usesUPS = () => state.enabled && state.shipping === 'Customer UPS Account';
  const fields = () => [...document.querySelectorAll('easyusa-dropship')];
  let state, revision = 0, savedRevision = 0, pending, checkoutBusy = false, replay = null;
  let message = '', failed = false;
  const attributes = () => ({
    'Dropship Order': state.enabled ? 'Yes' : '',
    'Shipping Arrangement': state.enabled ? state.shipping : '',
    'Sender Name Request': state.enabled && state.sender ? 'Yes' : '',
    'Shipping Coordination Required': state.enabled && (state.shipping !== 'EasyUSA' || state.sender) ? 'Yes' : '',
    'UPS Account Number': usesUPS() && state.upsAuthorized && validAccount(state.upsAccount) ? state.upsAccount : '',
    'UPS Billing Authorized': usesUPS() && state.upsAuthorized && validAccount(state.upsAccount) ? 'Yes' : '',
    'Packing Slip': state.enabled ? state.packing : ''
  });
  function render() {
    for (const el of fields()) {
      const toggle = el.querySelector('[data-dropship-toggle]');
      toggle.disabled = false;
      toggle.checked = state.enabled;
      toggle.setAttribute('aria-expanded', String(state.enabled));
      el.querySelector('[data-dropship-details]').hidden = !state.enabled;
      el.querySelector('[data-dropship-packing]').value = state.packing;
      el.querySelector('[data-dropship-custom]').hidden = state.packing !== 'Customer Provided' || el.dataset.emailReady !== 'true';
      el.querySelector('[data-dropship-shipping]').value = state.shipping;
      el.querySelector('[data-dropship-sender]').checked = state.sender;
      el.querySelector('[data-dropship-easyusa]').hidden = state.shipping !== 'EasyUSA';
      el.querySelector('[data-dropship-arranged]').hidden = state.shipping === 'EasyUSA';
      el.querySelector('[data-dropship-sender-help]').hidden = !state.sender;
      el.querySelector('[data-dropship-ups]').hidden = !usesUPS();
      el.querySelector('[data-dropship-labels]').hidden = state.shipping !== 'Customer Labels';
      const account = el.querySelector('[data-dropship-ups-account]');
      account.disabled = !usesUPS() || el.dataset.shippingReady !== 'true';
      if (account.value !== state.upsAccount) account.value = state.upsAccount;
      const authorization = el.querySelector('[data-dropship-ups-authorized]');
      authorization.disabled = account.disabled;
      authorization.checked = state.upsAuthorized;
      const statusNode = el.querySelector('[data-dropship-status]');
      if (statusNode.textContent !== message) statusNode.textContent = message;
      el.querySelector('[data-dropship-retry]').hidden = !failed;
    }
  }
  function status(text, error = false) { message = text; failed = error; render(); }
  // HideShip's current inputs cannot read cart attributes, so a $0 marker mirrors the arrangement.
  const markerIds = () => {
    const el = fields()[0];
    if (el?.dataset.shippingReady !== 'true' || !el.dataset.upsMarker || !el.dataset.labelsMarker) return null;
    return { 'Customer UPS Account': el.dataset.upsMarker, 'Customer Labels': el.dataset.labelsMarker };
  };
  // Markers can outlive the choice that added them (e.g. a restored cart keeps lines but not attributes).
  let reconciled = false;
  const markerMismatch = () => {
    const ids = markerIds();
    if (!ids || !state) return false;
    const present = (fields()[0]?.dataset.cartMarkers || '').split(',').filter(Boolean);
    const wanted = state.enabled ? ids[state.shipping] : undefined;
    return present.some(id => id !== wanted) || (!!wanted && !present.includes(wanted));
  };
  async function syncMarkers() {
    const ids = markerIds();
    if (!ids) return;
    const response = await fetch(root + 'cart.js', { cache: 'no-store', signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error('Cart reload failed');
    const items = (await response.json()).items || [];
    const markers = Object.values(ids);
    const hasProducts = items.some(item => !markers.includes(String(item.variant_id)));
    const wanted = state.enabled && hasProducts ? ids[state.shipping] : undefined;
    const updates = {};
    for (const id of markers) {
      const quantity = items.filter(item => String(item.variant_id) === id).reduce((sum, item) => sum + item.quantity, 0);
      const target = id === wanted ? 1 : 0;
      if (quantity !== target) updates[id] = target;
    }
    if (!Object.keys(updates).length) return;
    const result = await fetch(root + 'cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }), signal: AbortSignal.timeout(15000)
    });
    if (!result.ok) throw new Error('Shipping selection could not be saved');
    const savedItems = (await result.json()).items;
    if (!Array.isArray(savedItems)) throw new Error('Shipping selection could not be verified');
    for (const id of markers) {
      const quantity = savedItems.filter(item => String(item.variant_id) === id).reduce((sum, item) => sum + item.quantity, 0);
      if (quantity !== (id === wanted ? 1 : 0)) throw new Error('Shipping selection did not save');
    }
  }
  async function save() {
    if (pending) return pending;
    pending = (async () => {
      do {
        while (savedRevision < revision) {
          const version = revision;
          const payload = attributes();
          if (state.enabled && state.packing === 'Customer Provided' && !fields().some(el => el.dataset.emailReady === 'true')) {
            throw new Error('Packing slip email is not configured');
          }
          status('Saving order instructions…');
          const response = await fetch(root + 'cart/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: payload }), signal: AbortSignal.timeout(15000)
          });
          if (!response.ok) throw new Error('Cart update failed');
          const cart = await response.json();
          for (const [key, value] of Object.entries(payload)) {
            if ((cart.attributes?.[key] || '') !== value) throw new Error('Cart instructions did not save');
          }
          savedRevision = version;
        }
        await syncMarkers();
      } while (savedRevision < revision);
      status('Order instructions saved.');
    })().catch(error => {
      status('Your order instructions could not be saved. Retry before checkout.', true);
      throw error;
    }).finally(() => { pending = null; });
    return pending;
  }
  function change() { revision++; render(); save().catch(() => {}); }
  class DropshipCart extends HTMLElement {
    connectedCallback() {
      if (!state) state = { enabled: this.dataset.enabled === 'true', packing: this.dataset.packing === 'Customer Provided' ? 'Customer Provided' : 'None', shipping: shippingMode(this.dataset.shipping), sender: this.dataset.sender === 'true', upsAccount: this.dataset.upsAccount || '', upsAuthorized: this.dataset.upsAuthorized === 'true' };
      render();
      if (!reconciled && markerMismatch()) { reconciled = true; change(); }
      // Horizon can morph children without reconnecting the custom element.
      this.observer?.disconnect();
      this.observer = new MutationObserver(() => render());
      this.observer.observe(this, { childList: true, subtree: true });
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  }
  window.EasyUSADropship = { save };
  customElements.define('easyusa-dropship', DropshipCart);
  document.addEventListener('change', event => {
    const el = event.target;
    if (!el.closest('easyusa-dropship')) return;
    if (el.matches('[data-dropship-toggle]')) {
      state.enabled = el.checked;
      if (!state.enabled) { state.packing = 'None'; state.shipping = 'EasyUSA'; state.sender = false; state.upsAccount = ''; state.upsAuthorized = false; }
      change();
    } else if (el.matches('[data-dropship-shipping]')) { state.shipping = shippingMode(el.value); state.upsAccount = ''; state.upsAuthorized = false; change(); }
    else if (el.matches('[data-dropship-ups-authorized]') && !el.disabled) { state.upsAuthorized = el.checked; change(); }
    else if (el.matches('[data-dropship-sender]')) { state.sender = el.checked; change(); }
    else if (el.matches('[data-dropship-packing]')) { state.packing = el.value; change(); }
  });
  document.addEventListener('input', event => {
    if (state && event.target.matches('[data-dropship-ups-account]') && !event.target.disabled) {
      state.upsAccount = event.target.value.trim().toUpperCase();
      state.upsAuthorized = false; // A changed account needs fresh billing authorization.
      change(); // Clears any formerly authorized account; draft digits stay in memory.
    }
  }, true);
  async function checkout(event, target, resume) {
    if (replay === target || event.defaultPrevented || !state || (!state.enabled && revision === savedRevision && !markerMismatch())) return;
    if (target.disabled || target.getAttribute('aria-disabled') === 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;
    try {
      revision++; // Verify again at the checkout boundary.
      await save();
      if (state.enabled && state.shipping !== 'EasyUSA' && !fields().some(el => el.dataset.shippingReady === 'true' && el.dataset.emailReady === 'true')) {
        status('Contact Easy USA to arrange shipping before checkout, or choose “Easy USA ships it”.');
        fields().find(el => el.getClientRects().length)?.querySelector('[data-dropship-shipping]')?.focus();
        return;
      }
      if (usesUPS() && (!validAccount(state.upsAccount) || !state.upsAuthorized)) {
        status(!validAccount(state.upsAccount) ? 'Enter your UPS account number (6–10 letters or numbers).' : 'Confirm that you are authorized to bill this UPS account before checkout.');
        fields().find(el => el.getClientRects().length)?.querySelector(!validAccount(state.upsAccount) ? '[data-dropship-ups-account]' : '[data-dropship-ups-authorized]')?.focus();
        return;
      }
      replay = target;
      resume(); // Replay normal handlers, including existing purchase eligibility checks.
    } catch { fields().find(el => el.getClientRects().length)?.querySelector('[data-dropship-retry]')?.focus(); }
    finally { replay = null; checkoutBusy = false; }
  }
  document.addEventListener('click', event => {
    const retry = event.target.closest('[data-dropship-retry]');
    if (retry) { save().catch(() => {}); return; }
    const target = event.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
    if (target) checkout(event, target, () => target.click());
  }, true);
  document.addEventListener('submit', event => {
    const isCartSubmit = event.target.id === 'cart-form';
    if (event.submitter?.name !== 'checkout' && !(isCartSubmit && !event.submitter)) return;
    // A replayed button click produces its own submit event synchronously.
    if (replay && replay === event.submitter) return;
    checkout(event, event.target, () => event.submitter ? event.target.requestSubmit(event.submitter) : event.target.requestSubmit());
  }, true);
  window.addEventListener('pageshow', async event => {
    if (!event.persisted || !state || revision !== savedRevision) return;
    const refreshRevision = revision;
    try {
      const response = await fetch(root + 'cart.js', { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart reload failed');
      const cart = await response.json();
      // A shopper may edit while the back-navigation refresh is in flight.
      if (revision !== refreshRevision) return;
      state.enabled = cart.attributes?.['Dropship Order'] === 'Yes';
      state.packing = cart.attributes?.['Packing Slip'] === 'Customer Provided' ? 'Customer Provided' : 'None';
      state.shipping = shippingMode(cart.attributes?.['Shipping Arrangement']);
      state.sender = cart.attributes?.['Sender Name Request'] === 'Yes';
      state.upsAccount = cart.attributes?.['UPS Account Number'] || '';
      state.upsAuthorized = cart.attributes?.['UPS Billing Authorized'] === 'Yes';
      status('');
    } catch {
      if (revision !== refreshRevision) return;
      revision++; status('Please retry saving your instructions before checkout.', true);
    }
  });
})();
